import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { ApiConstant, FormatConstant, KeyConstant, LangConstant, SystemConstant } from "const";
import { CommonUtils, StorageUtils, SystemUtils } from "utils";
import { useCreateUser, useDepartmentDetails, useUnitDetails, useUnitList, useUpdateUserAccount, useRole, useUserGroups } from "hooks";
import { format } from "date-fns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { PhoneTextField } from "../components";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import apisauce from "apisauce";
import SearchableSelect from "components/common/SearchableSelect";
import { IconSpeakerphone, IconUsers, IconChevronRight } from "@tabler/icons-react";
import UpdateChannelDialog from "pages/ChannelManagement/components/UpdateChannelDialog";
import { ACTIVE_FUNCTION_DISPLAY } from "./UserActiveFunction";

const UserInfoDialog = ({ data, isOpen, isEdit: initialIsEdit, onClose }) => {
  const { t: getLabel } = useTranslation();
  const { isServiceAdmin } = useRole();
  const { updateUserAccount } = useUpdateUserAccount();
  const { createUser } = useCreateUser();
  const { totalUnitList } = useUnitList();
  const { positions: positionsByUnit, departments, handleGetUnitDetails } = useUnitDetails();
  const { positions: positionsByDepartment, handleGetDepartmentDetails } = useDepartmentDetails();

  const userDetail = getLabel(LangConstant.OBJ_USER_DETAIL, { returnObjects: true });

  const [isEditMode, setIsEditMode] = useState(initialIsEdit);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [phone, setPhone] = useState(data?.phone || "");
  const [regionCode, setRegionCode] = useState(data?.regionCode || "");
  const [unit, setUnit] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");
  const [activeFunctionDisplay, setActiveFunctionDisplay] = useState(null);
  const [dialogContent, setDialogContent] = useState(null);
  const [positions, setPositions] = useState([]);
  const [dataBirthday, setDataBirthday] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isLoadingAvatar, setIsLoadingAvatar] = useState(Boolean(data?.avatarId));

  const accountId = data?.accountId || data?.account_id || data?.id;
  const { allItems: userGroups, channelCount, groupCount, isLoading: isLoadingGroups, refetch: refetchGroups } = useUserGroups(accountId, isOpen && !isEditMode);
  const [groupTab, setGroupTab] = useState("all");
  const [selectedGroup, setSelectedGroup] = useState(null);

  const filteredGroups = useMemo(() => {
    if (groupTab === "channel") return userGroups.filter(g => g.groupType === 2);
    if (groupTab === "group") return userGroups.filter(g => g.groupType === 1);
    return userGroups;
  }, [userGroups, groupTab]);

  // Fetch avatar from TUS with auth
  useEffect(() => {
    if (!data?.avatarId || !accountId) {
      setAvatarUrl(null);
      setIsLoadingAvatar(false);
      return;
    }
    const tusDomain = StorageUtils.getStoreData(KeyConstant.KEY_TUS_DOMAIN);
    const accessToken = StorageUtils.getStoreData(KeyConstant.KEY_ACCESS_TOKEN);
    if (!tusDomain || !accessToken) {
      setAvatarUrl(null);
      setIsLoadingAvatar(false);
      return;
    }
    let cancelled = false;
    setIsLoadingAvatar(true);
    const fetchAvatar = async () => {
      try {
        const url = "https://" + tusDomain + "/files/accounts/" + accountId + "/" + data.avatarId;
        const api = apisauce.create({
          baseURL: url,
          timeout: ApiConstant.TIMEOUT,
          responseType: "blob",
          headers: { authorization: `Bearer ${accessToken}` },
        });
        const response = await api.get("");
        if (!cancelled && response.ok && response.data) {
          const objectUrl = URL.createObjectURL(response.data);
          setAvatarUrl(objectUrl);
        }
      } catch (e) {
        // Silently fail — show fallback avatar
      } finally {
        if (!cancelled) setIsLoadingAvatar(false);
      }
    };
    fetchAvatar();
    return () => {
      cancelled = true;
      if (avatarUrl) URL.revokeObjectURL(avatarUrl);
    };
  }, [data?.avatarId, accountId]);

  const isCreate = data === null && initialIsEdit === true && isOpen === true;
  const isValid = isValidForm(data, { phone, name, email, unit, department, position }, isServiceAdmin);

  // Sorted options for searchable selects
  const sortedUnitOptions = useMemo(() =>
    [...totalUnitList].sort((a, b) => (a.unitName || "").localeCompare(b.unitName || "")).map(u => ({ value: u.unitId, label: u.unitName })),
    [totalUnitList]
  );
  const sortedDepartmentOptions = useMemo(() =>
    [...departments].sort((a, b) => (a.departmentName || "").localeCompare(b.departmentName || "")).map(d => ({ value: d.departmentId, label: d.departmentName })),
    [departments]
  );
  const sortedPositionOptions = useMemo(() =>
    [...positions].sort((a, b) => (a.positionName || "").localeCompare(b.positionName || "")).map(p => ({ value: p.positionId, label: p.positionName })),
    [positions]
  );

  const handleEscKey = useCallback((e) => {
    if (e.key === "Escape") {
      if (document.querySelector(".MuiPopover-root, .MuiPopper-root, .MuiDialog-root")) return;
      if (dialogContent) return;
      onClose();
    }
  }, [onClose, dialogContent]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscKey);
    }
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [isOpen, handleEscKey]);

  useEffect(() => {
    setIsEditMode(initialIsEdit);
  }, [initialIsEdit]);

  const handleChangeName = event => setName(event.target.value);
  const handleChangeEmail = event => setEmail(event.target.value);
  const handleChangeBirthday = event => setDataBirthday(event);

  const handleChangeUnit = value => {
    setUnit(value);
    setDepartment("");
    setPosition("");
    handleGetUnitDetails(value);
  };

  const handleChangeDepartment = value => {
    setDepartment(value);
    setPosition("");
    handleGetDepartmentDetails(value);
  };

  const handleChangePosition = value => setPosition(value);

  const handleSwitchToEdit = () => setIsEditMode(true);

  const handleCancelEdit = () => {
    // Reset form data back to original
    if (data) {
      setName(data.name || "");
      setEmail(data.mail || "");
      setPhone(data.phone || "");
      setRegionCode(data.dialCode || "");
      setUnit(data.unitId || "");
      setDepartment(data.departmentId || "");
      setPosition(data.positionId || "");
      if (data.birthday) {
        const [day, month, year] = data.birthday.split(/[-\/]/).map(Number);
        setDataBirthday(new Date(year, month - 1, day));
      } else {
        setDataBirthday(null);
      }
    }
    setIsEditMode(false);
  };

  const handleEditUser = () => {
    const userPhone = phone.replace(new RegExp("^" + regionCode), "");
    const parsePhoneNumber = parsePhoneNumberFromString(userPhone, countryCode.toUpperCase());
    const birthday = getReqBirthdayFormat(dataBirthday);

    if (isServiceAdmin) {
      if (Boolean(name) && name !== "" && name.length > 255) {
        setDialogContent(getLabel("TXT_DATA_WRONG_FORMAT"));
      } else {
        updateUserAccount({
          ...data,
          name: name,
          regionCode: regionCode,
          phone: parsePhoneNumber.number,
          unitId: unit,
          departmentId: department,
          positionId: position,
          onClose: onClose,
        });
      }
    } else {
      if (Boolean(email) && email !== "" && (!CommonUtils.validateEmail(email) || email.length > 255)) {
        setDialogContent(getLabel(LangConstant.TXT_EMAIL_WRONG));
      } else if (Boolean(name) && name !== "" && name.length > 255) {
        setDialogContent(getLabel("TXT_DATA_WRONG_FORMAT"));
      } else {
        updateUserAccount({
          ...data,
          name: name,
          mail: email,
          birthday: birthday,
          unitId: unit,
          departmentId: department,
          positionId: position,
          onClose: onClose,
        });
      }
    }
  };

  const handleCreateUser = () => {
    const userPhone = phone.replace(new RegExp("^" + regionCode), "");
    const parsePhoneNumber = parsePhoneNumberFromString(userPhone, countryCode.toUpperCase());
    const birthday = getReqBirthdayFormat(dataBirthday);

    if (isServiceAdmin) {
      if (!name || name.length > 255 || phone.length <= 1) {
        SystemUtils.appendNotification(getLabel("TXT_DATA_WRONG_FORMAT"), "error");
      } else {
        createUser({
          ...data,
          name: name,
          regionCode: regionCode,
          phone: parsePhoneNumber.number,
          unitId: unit,
          departmentId: department,
          positionId: position,
          onClose: onClose,
        });
      }
    } else {
      if (Boolean(email) && email !== "" && (!CommonUtils.validateEmail(email) || email.length > 255)) {
        setDialogContent(getLabel(LangConstant.TXT_EMAIL_WRONG));
      } else if (Boolean(name) && name !== "" && name.length > 255) {
        setDialogContent(getLabel("TXT_DATA_WRONG_FORMAT"));
      } else {
        createUser({
          ...data,
          name: name,
          mail: email,
          birthday: birthday,
          regionCode: regionCode,
          phone: parsePhoneNumber.number,
          unitId: unit,
          departmentId: department,
          positionId: position,
          onClose: onClose,
        });
      }
    }
  };

  const handleCloseNotification = () => setDialogContent(null);

  const handleChangePhone = (value, data) => {
    setPhone(value);
    setRegionCode(data?.dialCode || "");
    setCountryCode(data?.countryCode || "");
  };

  useEffect(() => {
    if (data) {
      setEmail(data.mail || "");
      setName(data.name || "");
      setPhone(data.phone || "");
      setRegionCode(data.dialCode || "");
      setUnit(data.unitId || "");
      setDepartment(data.departmentId || "");
      setPosition(data.positionId || "");

      if (data.birthday) {
        const [day, month, year] = data.birthday.split(/[-\/]/).map(Number);
        setDataBirthday(new Date(year, month - 1, day));
      } else {
        setDataBirthday(null);
      }

      if (data.unitId) handleGetUnitDetails(data.unitId);
      if (data.departmentId) handleGetDepartmentDetails(data.unitId);

      // Hiển thị giá trị cột "Trạng thái hoạt động" (gộp từ branch_status + state).
      const activeFunction = SystemConstant.resolveActiveFunction(data);
      setActiveFunctionDisplay(activeFunction ? ACTIVE_FUNCTION_DISPLAY[activeFunction] : null);
    } else {
      setEmail("");
      setDataBirthday(null);
      setName("");
      setPhone("");
      setRegionCode("");
      setUnit("");
      setDepartment("");
      setPosition("");
      setActiveFunctionDisplay(null);
    }
  }, [data]);

  useEffect(() => {
    if (positionsByUnit) {
      let newListPos = positionsByUnit.filter(item => item.departmentId === department);
      setPositions(newListPos);
    }
  }, [positionsByUnit]);

  useEffect(() => {
    if (positionsByDepartment) setPositions(positionsByDepartment);
  }, [positionsByDepartment]);

  if (!isOpen) return null;

  // Helper to get display values for view mode
  const getUnitName = () => totalUnitList.find(u => u.unitId === unit)?.unitName || "--";
  const getDepartmentName = () => departments.find(d => d.departmentId === department)?.departmentName || "--";
  const getPositionName = () => positions.find(p => p.positionId === position)?.positionName || "--";
  const getBirthdayDisplay = () => dataBirthday ? format(dataBirthday, FormatConstant.FM_DD_MM_YYYY) : "--";

  // VIEW MODE - 2-column layout: left (avatar, identity & work info), right (channel & group activities)
  const renderViewMode = () => {
    const branchName = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_NAME) || "--";

    return (
      <div className="modal-body" style={{ overflowY: "auto", overflowX: "hidden", maxHeight: "80vh", padding: "24px" }}>
        <div className="d-flex gap-4" style={{ minHeight: 320 }}>
          {/* Left block: Avatar, Identity & Work info */}
          <div
            className="d-flex flex-column"
            style={{
              width: 320,
              flexShrink: 0,
              borderRight: "1px solid var(--tblr-border-color, #e6e7e9)",
              paddingRight: 24,
            }}
          >
            {/* Avatar & Core identity */}
            <div className="d-flex flex-column align-items-center text-center">
              {isLoadingAvatar ? (
                <span
                  className="avatar avatar-xl avatar-rounded mb-3 placeholder-glow"
                  style={{ width: 84, height: 84, backgroundColor: "var(--tblr-border-color, #e6e7e9)" }}
                ></span>
              ) : avatarUrl ? (
                <span
                  className="avatar avatar-xl avatar-rounded mb-3"
                  style={{ backgroundImage: `url(${avatarUrl})`, width: 84, height: 84 }}
                ></span>
              ) : (
                <span
                  className="avatar avatar-xl avatar-rounded mb-3"
                  style={{ backgroundColor: CommonUtils.stringToColor(name), width: 84, height: 84, fontSize: 28, color: "#fff" }}
                >
                  {name?.charAt(0)?.toUpperCase()}
                </span>
              )}

              <h3 className="mb-1 fw-bold text-center" style={{ fontSize: 16, wordBreak: "break-word" }}>
                {name || "--"}
              </h3>
              <p className="text-muted mb-1" style={{ fontSize: 13 }}>{phone || "--"}</p>
              {email && <p className="text-muted mb-2" style={{ fontSize: 12, wordBreak: "break-all" }}>{email}</p>}

              {/* Trạng thái hoạt động — dùng chung class màu với cột trong bảng */}
              <div className="d-flex justify-content-center flex-wrap gap-1 mb-3">
                {activeFunctionDisplay && (
                  <span className={activeFunctionDisplay.badgeClass} style={{ fontSize: 11 }}>
                    {getLabel(activeFunctionDisplay.label)}
                  </span>
                )}
              </div>
            </div>

            {/* Work Info ("Thông tin công việc") */}
            <div className="w-100 mt-2 pt-3" style={{ borderTop: "1px solid var(--tblr-border-color, #e6e7e9)" }}>
              <h4 className="mb-2 text-muted text-uppercase" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.5px" }}>
                {getLabel("TXT_PROFILE_INFO")}
              </h4>
              <InfoRow label={getLabel("TXT_ORGANIZATION")} value={branchName} />
              <InfoRow label={getLabel("TXT_UNIT")} value={getUnitName()} />
              <InfoRow label={getLabel("TXT_DEPARTMENT")} value={getDepartmentName()} />
              <InfoRow label={getLabel("TXT_POSITION")} value={getPositionName()} />
              {!isServiceAdmin && (
                <InfoRow label={userDetail.birthday} value={getBirthdayDisplay()} />
              )}
            </div>
          </div>

          {/* Right block: User Activities (Channels & Groups) */}
          <div className="d-flex flex-column" style={{ flex: 1, minWidth: 0 }}>
            {/* Header & Tabs */}
            <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <h4 className="mb-0 fw-semibold" style={{ fontSize: 15 }}>
                  {getLabel("TXT_USER_ACTIVITIES")}
                </h4>
                <span className="badge bg-blue-lt" style={{ fontSize: 12, fontWeight: 600 }}>
                  {userGroups.length}
                </span>
              </div>

              <div className="btn-group btn-group-sm" role="group">
                <button
                  type="button"
                  className={`btn ${groupTab === "all" ? "btn-primary" : "btn-outline-secondary"}`}
                  style={{ fontSize: 12, padding: "3px 10px" }}
                  onClick={() => setGroupTab("all")}
                >
                  {getLabel("TXT_ALL") || "Tất cả"} ({userGroups.length})
                </button>
                <button
                  type="button"
                  className={`btn ${groupTab === "channel" ? "btn-primary" : "btn-outline-secondary"}`}
                  style={{ fontSize: 12, padding: "3px 10px" }}
                  onClick={() => setGroupTab("channel")}
                >
                  {getLabel("TXT_GROUP_TYPE_CHANNEL") || "Kênh"} ({channelCount})
                </button>
                <button
                  type="button"
                  className={`btn ${groupTab === "group" ? "btn-primary" : "btn-outline-secondary"}`}
                  style={{ fontSize: 12, padding: "3px 10px" }}
                  onClick={() => setGroupTab("group")}
                >
                  {getLabel("TXT_GROUP_TYPE_GROUP") || "Nhóm"} ({groupCount})
                </button>
              </div>
            </div>

            {/* Content List */}
            {isLoadingGroups ? (
              <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted gap-2">
                <div className="spinner-border spinner-border-sm text-primary" role="status"></div>
                <span style={{ fontSize: 13 }}>Đang tải danh sách hoạt động...</span>
              </div>
            ) : filteredGroups.length === 0 ? (
              <div
                className="text-muted text-center py-5 d-flex flex-column align-items-center justify-content-center"
                style={{
                  backgroundColor: "var(--tblr-bg-surface-secondary, #f8fafc)",
                  borderRadius: 8,
                  border: "1px dashed var(--tblr-border-color, #e6e7e9)",
                  minHeight: 220,
                }}
              >
                <IconUsers size={32} className="text-muted mb-2" style={{ opacity: 0.4 }} />
                <span style={{ fontSize: 13 }}>
                  {userGroups.length === 0
                    ? (getLabel("TXT_NO_CHANNELS_OR_GROUPS") || "Chưa tham gia kênh hoặc nhóm nào")
                    : "Không có dữ liệu phù hợp"}
                </span>
              </div>
            ) : (
              <div
                className="d-flex flex-column gap-2"
                style={{
                  maxHeight: 440,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingRight: 6,
                }}
              >
                {filteredGroups.map(item => {
                  const isChannel = item.groupType === 2;
                  const isAdmin = item.memberType === 0;

                  return (
                    <div
                      key={item.id}
                      className="d-flex align-items-center justify-content-between p-2 px-3"
                      style={{
                        backgroundColor: "var(--tblr-bg-surface-secondary, #f8fafc)",
                        borderRadius: 8,
                        border: "1px solid var(--tblr-border-color, #e6e7e9)",
                        transition: "all 0.15s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = "var(--tblr-bg-surface-tertiary, #f1f5f9)";
                        e.currentTarget.style.borderColor = "var(--tblr-primary, #206bc4)";
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = "var(--tblr-bg-surface-secondary, #f8fafc)";
                        e.currentTarget.style.borderColor = "var(--tblr-border-color, #e6e7e9)";
                      }}
                      onClick={() => setSelectedGroup(item)}
                    >
                      <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flex: 1 }}>
                        <span
                          className={`avatar avatar-sm avatar-rounded ${isChannel ? "bg-blue-lt text-blue" : "bg-green-lt text-green"}`}
                          style={{ width: 34, height: 34, flexShrink: 0 }}
                        >
                          {isChannel ? <IconSpeakerphone size={18} /> : <IconUsers size={18} />}
                        </span>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div
                            className="text-truncate fw-semibold text-primary-hover"
                            style={{ fontSize: 13, color: "var(--tblr-body-color, #1e293b)" }}
                            title={item.groupName}
                          >
                            {item.groupName || "--"}
                          </div>
                          <div className="text-muted" style={{ fontSize: 11, marginTop: 1 }}>
                            {item.memberCount != null ? `${item.memberCount} thành viên` : ""}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-1 ms-2" style={{ flexShrink: 0 }}>
                        <span className={`badge ${isChannel ? "bg-blue-lt" : "bg-green-lt"}`} style={{ fontSize: 11 }}>
                          {isChannel ? (getLabel("TXT_GROUP_TYPE_CHANNEL") || "Kênh") : (getLabel("TXT_GROUP_TYPE_GROUP") || "Nhóm")}
                        </span>
                        {isAdmin ? (
                          <span className="badge bg-orange-lt" style={{ fontSize: 11 }}>
                            {getLabel("TXT_ROLE_ADMIN") || "Trưởng nhóm"}
                          </span>
                        ) : (
                          <span className="badge bg-secondary-lt" style={{ fontSize: 11 }}>
                            {getLabel("TXT_ROLE_MEMBER") || "Thành viên"}
                          </span>
                        )}
                        <IconChevronRight size={16} className="text-muted ms-1" style={{ opacity: 0.6 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // EDIT MODE - form inputs
  const renderEditMode = () => (
    <div className="modal-body" style={{ overflowY: "auto", maxHeight: "70vh" }}>
      <div className="mb-3">
        <label className="form-label">
          {userDetail.fullName} <span className="text-danger">*</span>
        </label>
        <input
          className="form-control"
          type="text"
          value={name}
          onChange={handleChangeName}
          name="full-name"
        />
      </div>
      <div className="mb-3">
        <PhoneTextField
          value={phone}
          onChange={handleChangePhone}
          label={userDetail.phone}
          required
          disabled={!isCreate}
        />
      </div>
      <div className="mb-3">
        <label className="form-label">{getLabel("TXT_UNIT")}</label>
        <SearchableSelect
          value={unit}
          onChange={handleChangeUnit}
          placeholder={getLabel("TXT_CHOOSE_UNIT")}
          options={sortedUnitOptions}
        />
      </div>
      <div className="mb-3">
        <label className="form-label">{getLabel("TXT_DEPARTMENT")}</label>
        <SearchableSelect
          value={department}
          onChange={handleChangeDepartment}
          placeholder={getLabel("TXT_CHOOSE_DEPARTMENT")}
          disabled={unit === ""}
          options={sortedDepartmentOptions}
        />
      </div>
      <div className="mb-3">
        <label className="form-label">{getLabel("TXT_POSITION")}</label>
        <SearchableSelect
          value={position}
          onChange={handleChangePosition}
          placeholder={getLabel("TXT_CHOOSE_POSITION")}
          options={sortedPositionOptions}
        />
      </div>
      {!isServiceAdmin && (
        <>
          <div className="mb-3">
            <label className="form-label">
              {userDetail.email} <span className="text-danger">*</span>
            </label>
            <input
              className="form-control"
              type="email"
              value={email}
              onChange={handleChangeEmail}
              name="email"
            />
          </div>
          <div className="mb-3">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={userDetail.birthday}
                value={dataBirthday}
                format={FormatConstant.FM_DD_MM_YYYY}
                disableFuture={true}
                onChange={handleChangeBirthday}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    name: "birthday",
                    InputLabelProps: { shrink: true },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Fragment>
      <div className="modal-backdrop fade show"></div>
      <div className="modal modal-blur fade show d-block" tabIndex="-1" role="dialog">
        <div
          className={`modal-dialog modal-dialog-centered ${!isCreate && !isEditMode ? "modal-xl" : ""}`}
          style={{
            maxWidth: !isCreate && !isEditMode ? 920 : 540,
            width: "95%",
          }}
          role="document"
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {getLabel(isCreate ? LangConstant.TXT_ADD_NEW_USER : isEditMode ? LangConstant.TXT_EDIT_USER_INFO : LangConstant.TXT_DETAILS_INFO)}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </div>

            {isCreate || isEditMode ? renderEditMode() : renderViewMode()}

            <div className="modal-footer d-flex gap-2" style={{ padding: "16px 24px" }}>
              {isCreate ? (
                <button
                  className="btn btn-primary w-100"
                  onClick={handleCreateUser}
                  disabled={!isValid}
                >
                  {getLabel(LangConstant.L_ADD_BUTTON)}
                </button>
              ) : isEditMode ? (
                <>
                  <button className="btn btn-secondary flex-fill" onClick={handleCancelEdit}>
                    {getLabel(LangConstant.L_CANCEL)}
                  </button>
                  <button
                    className="btn btn-primary flex-fill"
                    onClick={handleEditUser}
                    disabled={!isValid}
                  >
                    {getLabel(LangConstant.L_UPDATE)}
                  </button>
                </>
              ) : (
                <>
                  <button className="btn btn-secondary flex-fill" onClick={onClose}>
                    {getLabel(LangConstant.L_CLOSE)}
                  </button>
                  <button className="btn btn-primary flex-fill" onClick={handleSwitchToEdit}>
                    {getLabel(LangConstant.L_UPDATE)}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Group / Channel Detail & Members Dialog */}
      {Boolean(selectedGroup) && (
        <UpdateChannelDialog
          open={Boolean(selectedGroup)}
          channel={selectedGroup}
          onClose={() => {
            setSelectedGroup(null);
            refetchGroups();
          }}
          zIndex={1065}
        />
      )}

      {/* Notification Dialog */}
      {Boolean(dialogContent) && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1060 }}></div>
          <div className="modal modal-blur fade show d-block" style={{ zIndex: 1061 }} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-sm modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-body">
                  <p style={{ color: "#000000", marginTop: 10 }}>{dialogContent}</p>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={handleCloseNotification}>
                    {getLabel(LangConstant.L_OK)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </Fragment>
  );
};

export default UserInfoDialog;

UserInfoDialog.propTypes = {
  data: PropTypes.object,
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onClose: PropTypes.func,
};

UserInfoDialog.defaultProps = {
  onClose: () => { },
};

// Info row component for view mode
const InfoRow = ({ label, value }) => (
  <div
    className="d-flex justify-content-between align-items-center py-2"
    style={{ borderBottom: "1px solid var(--tblr-border-color, #f1f3f5)" }}
  >
    <span className="text-muted" style={{ fontSize: 13, flexShrink: 0, width: 95 }}>
      {label}
    </span>
    <span
      className="fw-medium text-end"
      style={{ fontSize: 13, minWidth: 0, flex: 1, wordBreak: "break-word" }}
      title={typeof value === "string" ? value : undefined}
    >
      {value || "--"}
    </span>
  </div>
);

const isValidForm = (data, newData, isServiceAdmin) => {
  const isEdit = Boolean(data?.id);
  const { phone, name, email, unit, department, position } = newData || {};
  const isValidForm = phone && name && (isServiceAdmin || email);
  const isChanged =
    name !== data?.name ||
    phone !== data?.phone ||
    unit !== data?.unitId ||
    department !== data?.departmentId ||
    position !== data?.positionId ||
    (!isServiceAdmin && email !== data?.mail);
  return isEdit ? isValidForm && isChanged : isValidForm;
};

const getReqBirthdayFormat = birthday => {
  if (!birthday) return null;
  return format(birthday, FormatConstant.FM_DD_MM_YYYY);
};
